import { parseLoggedUserToken } from './auth';
import { firstEl, lastEl } from '@go-shared/lib/functional';
import { BareSailing, BareSailingDay } from '@go-shared/types/sailing';
import { addDaysToUTCDate, isoDate } from '@go-shared/lib/time';
import { getSailings } from './api/itinerary';
import { safeIdCompare } from '@go-shared/lib/utils';

/**
 * A day is locked if the locked flag is set to true and the logged user is not an admin
 *
 */
export function isDayLocked(locked: boolean): boolean {
  if (!locked) return false;

  try {
    let loggedUser = parseLoggedUserToken();
    return !loggedUser.shipAdmin && !loggedUser.superAdmin;
  } catch (_e) {
    // If we couldn't get the user info, err on the safe side and return locked
    return true;
  }
}

const CALC_ADJ_SAIL_DAY_BUFFER = 15; // two weeks forward and back

// This function attemps to find the adjacent sailing days after a given sailing and start idx.
// If the adjacent sailing day is within the sailing, then the calculation is simple. If it is not,
// then it attempts to find the adjacent sailing up to CALC_ADJ_SAIL_DAY_BUFFER days after the last
// date of the last sailing day or up to CALC_ADJ_SAIL_DAY_BUFFER days before the date of the first
// sailing day. If it cannot find one in that timeframe, it returns undefined.
export async function attemptCalcAdjacentSailingDay(sailing: BareSailing, startIdx: number) {
  // can't provide a startIdx that is not within the sailing days
  if (startIdx < 0 || startIdx > sailing.days.length - 1) return undefined;

  let nextSailing: BareSailing | undefined = sailing;
  let nextSailingDay: BareSailingDay | undefined = sailing.days[startIdx + 1];

  let prevSailing: BareSailing | undefined = sailing;
  let prevSailingDay: BareSailingDay | undefined = sailing.days[startIdx - 1];

  if (!nextSailingDay || !prevSailingDay) {
    // need to grab sailings around this sailing
    const startDate = addDaysToUTCDate(firstEl(sailing.days).date, -CALC_ADJ_SAIL_DAY_BUFFER);
    const endDate = addDaysToUTCDate(lastEl(sailing.days).date, CALC_ADJ_SAIL_DAY_BUFFER);

    const sailings = await getSailings(sailing.property, isoDate(startDate), isoDate(endDate));
    const mySailingIdx = sailings.findIndex(s => safeIdCompare(s._id, sailing._id));

    if (!prevSailingDay) {
      // this means I need to get it from the previous sailing
      prevSailing = sailings[mySailingIdx - 1];
      prevSailingDay = prevSailing ? lastEl(prevSailing.days) : undefined;
    }

    if (!nextSailingDay) {
      // this means I need to get it from the next sailing
      nextSailing = sailings[mySailingIdx + 1];
      nextSailingDay = nextSailing ? firstEl(nextSailing.days) : undefined;
    }
  }

  const nextSailingBundle =
    nextSailing && nextSailingDay
      ? { sailing: nextSailing, sailingDay: nextSailingDay }
      : undefined;

  const prevSailingBundle =
    prevSailing && prevSailingDay
      ? { sailing: prevSailing, sailingDay: prevSailingDay }
      : undefined;

  return {
    prevSailingBundle,
    nextSailingBundle,
  };
}
